import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePageMeta } from '../lib/usePageMeta';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    { "@type": "Question", "name": "What does a remove extra spaces tool do?", "acceptedAnswer": { "@type": "Answer", "text": "It automatically cleans unnecessary spaces from text, removing duplicate spaces between words to ensure clean and consistent formatting." } },
    { "@type": "Question", "name": "Why should I remove extra spaces from my text?", "acceptedAnswer": { "@type": "Answer", "text": "Extra spaces can make content look unprofessional and harder to read. Cleaning them up ensures proper formatting for publishing." } },
    { "@type": "Question", "name": "Can I use this tool for long articles or documents?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, you can paste large sections of text, and the tool will instantly remove unnecessary spaces without altering words or sentences." } },
    { "@type": "Question", "name": "Will this tool change the content of my text?", "acceptedAnswer": { "@type": "Answer", "text": "No, it only removes extra spaces and does not modify the actual words, grammar, or meaning of the text." } },
    { "@type": "Question", "name": "Is this remove extra spaces tool free to use?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, it's completely free and works directly in your browser. You can paste or type text for instant cleanup." } },
    { "@type": "Question", "name": "Does the tool store the text I enter?", "acceptedAnswer": { "@type": "Answer", "text": "No, text is processed directly in your browser and not stored or saved on any server, ensuring content privacy." } }
  ]
};

const faqs = [
  { q: "What does a remove extra spaces tool do?", a: "It automatically cleans unnecessary spaces from text, removing duplicate spaces between words to ensure clean and consistent formatting." },
  { q: "Why should I remove extra spaces from my text?", a: "Extra spaces can make content look unprofessional and harder to read. Cleaning them up ensures proper formatting for publishing." },
  { q: "Can I use this tool for long articles or documents?", a: "Yes, you can paste large sections of text, and the tool will instantly remove unnecessary spaces without altering words or sentences." },
  { q: "Will this tool change the content of my text?", a: "No, it only removes extra spaces and does not modify the actual words, grammar, or meaning of the text." },
  { q: "Is this remove extra spaces tool free to use?", a: "Yes, it's completely free and works directly in your browser. You can paste or type text for instant cleanup." },
  { q: "Does the tool store the text I enter?", a: "No, text is processed directly in your browser and not stored or saved on any server, ensuring content privacy." },
];

function removeExtraSpaces(text) {
  return text
    .split('\n')
    .map(line => line.replace(/  +/g, ' ').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n');
}

export default function RemoveSpacesTool() {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [cleaned, setCleaned] = useState(false);

  usePageMeta({
    title: 'Remove Extra Spaces Online for Free - Wricoo',
    description: 'Remove extra spaces from your text instantly with this free online tool. Clean up formatting in articles, documents, and web content in seconds.',
    robots: 'index, follow',
    canonical: 'https://wricoo.com/tools/remove-extra-spaces-online/',
  });

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-schema-remove-spaces';
    script.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('faq-schema-remove-spaces');
      if (el) el.remove();
    };
  }, []);

  const handleClean = useCallback(() => {
    setText(prev => {
      const result = removeExtraSpaces(prev);
      setCleaned(true);
      setTimeout(() => setCleaned(false), 2000);
      return result;
    });
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="remove-spaces-page">

      {/* Header */}
      <header className="border-b border-blue-200 bg-blue-600 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/tools/" className="text-blue-200 hover:text-white transition"><ArrowLeft className="w-5 h-5" /></Link>
            <Link to="/" className="text-xl font-bold text-white tracking-tight">Wricoo</Link>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/about/" className="text-sm font-medium text-blue-100 hover:text-white transition">About</Link>
            <Link to="/pricing/" className="text-sm font-medium text-blue-100 hover:text-white transition">Pricing</Link>
            <Link to="/tools/" className="text-sm font-medium text-blue-100 hover:text-white transition">Tools</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-white hover:bg-blue-500 hover:text-white" onClick={() => navigate('/login/')}>Sign In</Button>
            <Button size="sm" className="bg-white text-blue-600 hover:bg-blue-50" onClick={() => navigate('/register/')}>Get Started</Button>
          </div>
        </div>
      </header>

      {/* Tool Section */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h1 className="font-bold text-gray-900 leading-tight mb-6 text-center">Remove Extra Spaces Online for Free</h1>

          {/* Internal Links */}
          <div className="flex flex-wrap gap-3 justify-center mb-10">
            <a href="/tools/character-count-online/" className="border border-gray-200 rounded-lg px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition">Character Count Online</a>
            <a href="/tools/word-count-online/" className="border border-gray-200 rounded-lg px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition">Word Counter Online</a>
            <a href="/tools/case-converter-online/" className="border border-gray-200 rounded-lg px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition">Case Converter Online</a>
            <a href="/tools/ai-seo-content-brief-generator/" className="border border-gray-200 rounded-lg px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition">AI SEO Content Brief Generator</a>
          </div>

          {/* Text Input */}
          <textarea
            className="w-full h-64 border border-gray-300 rounded-lg p-4 text-gray-800 resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
            placeholder="Paste or type your text here, then click the button below to remove extra spaces..."
            value={text}
            onChange={(e) => { setText(e.target.value); setCleaned(false); }}
            data-testid="text-input"
          />

          <div className="flex items-center gap-4">
            <Button onClick={handleClean} data-testid="remove-spaces-btn">
              Remove Extra Spaces
            </Button>
            {cleaned && <span className="text-sm text-emerald-600 font-medium" data-testid="cleaned-msg">Extra spaces removed.</span>}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-bold text-gray-900 mb-4">Clean Text. Clean Workflow.</h2>
          <p className="text-gray-600 leading-relaxed mb-8 max-w-2xl mx-auto">Formatting fixes are easy here, but managing content across spreadsheets and documents isn't. Move your SEO briefs, writers, and editors into one streamlined workspace built for content teams.</p>
          <Button size="lg" className="h-12 px-8" onClick={() => navigate('/pricing/')}>
            Start Your 30-Day Free Trial. No Credit Card Needed. <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="font-bold text-gray-900 mb-8">Remove Extra Spaces Online FAQs</h2>
          <div className="space-y-8">
            {faqs.map((faq, i) => (
              <div key={i} data-testid={`faq-item-${i}`}>
                <h3 className="text-base font-semibold text-gray-900 mb-2">Q. {faq.q}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} Wricoo. All rights reserved.</p>
          <nav className="flex gap-4 text-sm text-gray-500">
            <a href="/about/" className="hover:text-gray-900 transition">About</a>
            <a href="/pricing/" className="hover:text-gray-900 transition">Pricing</a>
            <a href="/tools/" className="hover:text-gray-900 transition">Tools</a>
            <a href="/privacy-policy/" className="hover:text-gray-900 transition">Privacy Policy</a>
            <a href="/terms-and-conditions/" className="hover:text-gray-900 transition">Terms</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
